import api from '../../services/api';
import { generateKeyPair, exportPublicKey, toBase64url, wrapPrivateKeyForApp, unwrapPrivateKeyFromApp } from '../../utils/fieldEncryption';
import { memberOf, sortMembers } from './PeopleData';
import { forgetGroupKeys } from '../../utils/spaceKeys';
import { realmKind } from './groupRealms';

// ONE GROUP'S MANAGEMENT (nucleus.oS Groups.swift GroupModel + GroupMore.swift):
// its people, its invitations, its realms, your share into it. Every call is a
// /groups/<id>/… route — there is no personal fallback in here, on purpose.
//   members     GET …/members; promote POST …/members/<u>/promote;
//               demote POST …/members/<u>/demote {privacy_realm_id};
//               remove/leave DELETE …/members/<u>; realm PUT …/members/<u> {privacy_realm_id} (required)
//   invite      POST …/invitations {username, role, privacy_realm_id, enc_group_privkey?}; cancel DELETE …/invitations/<id>
//
// Every membership has a realm: what a member may do in the group. Two are built
// in: "No access", which grants nothing (`builtin`), and "Full Access", where its
// admins sit (the group's default realm). A new group also starts with an
// ordinary "Members", which reads everything; admins make the others.
//   settings    GET/PUT /groups/<g> {name, description}; DELETE /groups/<g>
//   your share  GET/DELETE …/share; change it on the consent page (to_group_id)

export async function loadGroup(groupId) {
    const g = await api.getGroup(groupId).catch(() => null);
    const admin = g?.my_role === 'admin';
    const [members, invitations, realms, share] = await Promise.all([
        api.getGroupMembers(groupId).then((r) => r?.members || []).catch(() => []),
        admin ? api.getGroupInvitations(groupId).then((r) => r?.invitations || []).catch(() => []) : Promise.resolve([]),
        api.getGroupPrivacyRealms(groupId).then((r) => (Array.isArray(r) ? r : r?.privacy_realms || r?.realms || [])).catch(() => []),
        // a 404 is "you share nothing into it", not a failure
        api.getGroupShare(groupId).catch(() => null),
    ]);
    return {
        group: g,
        admin,
        members: sortMembers(members.map(memberOf)),
        pending: invitations.filter((i) => (i.status || 'pending') === 'pending').map((i) => ({
            id: String(i.id), name: i.invited_user_full_name || i.invited_user_name || 'Someone', username: i.invited_user_name || null, by: i.invited_by_full_name || i.invited_by_name || null, at: i.created_at || null,
            byId: i.invited_by_user_id ? String(i.invited_by_user_id) : null,
            role: i.role || 'member', realm: i.realm?.name || null,
            expires: i.expires_at || null,
        })),
        realms: memberRealms(realms),
        share: share && share.is_active !== false ? share : null,
    };
}

const BUILTIN_ORDER = { no_access: 0, full_access: 1 };

/** A group's realms as a member may be given them: the two built in first ("No
 *  access", "Full Access"), then the rest by name. A realm
 *  made for one recipient is never given to anyone else, so it is left out. */
export function memberRealms(list) {
    return (list || [])
        .filter((r) => r?.id && !(r.is_shared_realm && r.shared_with_id))
        .map((r) => ({
            id: String(r.id), name: r.name || 'Realm', description: r.description || '', color: r.color || null,
            builtin: r.builtin === 'no_access' ? r.builtin : null,
            kind: realmKind(r),
        }))
        .sort((a, b) => (BUILTIN_ORDER[a.kind] ?? 9) - (BUILTIN_ORDER[b.kind] ?? 9) || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

/** The realm a new or demoted member starts on: the group's No access. */
export const noAccessRealm = (realms) => (realms || []).find((r) => r.builtin === 'no_access') || null;
/** Where every admin sits: the group's Full Access. */
export const fullAccessRealm = (realms) => (realms || []).find((r) => r.kind === 'full_access') || null;

/** A server's refusal, said plainly: its own words, else ours. */
export function refusal(e, fallback) {
    if (e?.message) return e.message;
    if (e?.code === 'REALM_REQUIRED') return 'Every member needs a realm. Pick one of this group’s realms.';
    return fallback;
}

// A FRESH GROUP KEY (forward secrecy, prod GroupMembersTab): wrapped to every
// remaining member with a public key, then to every app that holds one. Needs no
// unlock — it wraps TO public keys. `ifEncrypted` skips a group that has no key
// (removal on an unencrypted group); the explicit re-key always runs.
// Returns { members, apps } or null when skipped.
export async function rotateGroupKey(groupId, { ifEncrypted = false } = {}) {
    const { members } = await api.request(`/groups/${groupId}/member-enc-keys`);
    if (ifEncrypted && !(members || []).some((m) => m.has_group_key)) return null;
    const withKeys = (members || []).filter((m) => m.enc_public_key);
    if (!withKeys.length) {
        if (ifEncrypted) return null;
        throw new Error('Nobody in the group has an encryption key yet.');
    }
    const kp = await generateKeyPair();
    const member_keys = [];
    for (const m of withKeys) {
        member_keys.push({ user_id: m.user_id, enc_group_privkey: await wrapPrivateKeyForApp(kp.privateKey, m.enc_public_key) });
    }
    await api.request(`/groups/${groupId}/enc-keys`, {
        method: 'POST',
        body: { group_enc_public_key: toBase64url(await exportPublicKey(kp.publicKey)), member_keys, rotate: true },
    });
    // what is written from now on is sealed to the new key
    forgetGroupKeys(groupId);
    let apps = 0;
    try {
        const r = await api.request(`/groups/${groupId}/apps`);
        for (const a of r?.apps || []) {
            if (!a.enc_public_key) continue;
            const enc_user_privkey = await wrapPrivateKeyForApp(kp.privateKey, a.enc_public_key);
            await api.request(`/groups/${groupId}/apps/${a.id}/enc-key`, { method: 'PUT', body: { enc_user_privkey } });
            apps += 1;
        }
    } catch (e) { console.warn('App re-provision after key rotation was partial:', e); }
    return { members: withKeys.length, apps };
}

// The group key re-wrapped for an invitee, when your own key is unlocked —
// so they are keyed the moment they accept. Best-effort: undefined otherwise,
// and a later re-key or backfill keys them.
export async function wrapForInvitee(groupId, userId, myPrivateKey) {
    if (!myPrivateKey || !userId) return undefined;
    try {
        const pub = await api.request(`/users/${userId}/enc-public-key`);
        if (!pub?.enc_public_key) return undefined;
        const gk = await api.request(`/groups/${groupId}/enc-privkey`);
        if (!gk?.enc_group_privkey) return undefined;
        const groupPriv = await unwrapPrivateKeyFromApp(gk.enc_group_privkey, myPrivateKey);
        return await wrapPrivateKeyForApp(groupPriv, pub.enc_public_key);
    } catch (e) {
        console.warn('Invite-time group key wrap skipped (keyed on re-key):', e);
        return undefined;
    }
}
